import type { CoverageState } from '@yucanvas/shared';

/**
 * Pin colours. Chosen for a phone held at arm's length in direct sun, so every
 * pair is separable by lightness as well as hue -- not by hue alone.
 */
export const COVERAGE_COLORS: Record<CoverageState, string> = {
  unvisited:      '#1d4ed8', // blue   — knock this
  not_home:       '#d97706', // amber  — come back
  contacted:      '#15803d', // green  — done
  skip:           '#57534e', // stone  — nothing to do here
  do_not_contact: '#b91c1c', // red    — do not knock
};

export const COVERAGE_LABELS: Record<CoverageState, string> = {
  unvisited:      'Not knocked',
  not_home:       'Not home',
  contacted:      'Contacted',
  skip:           'Skip',
  do_not_contact: 'Do not contact',
};

export const COVERAGE_ORDER: CoverageState[] = [
  'unvisited',
  'not_home',
  'contacted',
  'skip',
  'do_not_contact',
];

/** Map icon id per coverage state. Pure, so layers.ts can name icons without
 *  importing the canvas code that draws them. */
export const houseIconId = (state: CoverageState): string => `house-${state}`;

/** The pin shown where a canvasser has tapped to add a home, before saving. */
export const PROPOSED_ICON = 'house-proposed';


/**
 * The organize map's completion ramp: how much of a turf has been knocked.
 *
 * Sequential and single-hue, because the thing being encoded is an amount, not
 * a category -- a turf at 60% is not a different KIND of turf from one at 20%,
 * and a multi-hue ramp would say it was. Pale to deep, so "more colour" reads
 * as "more done", the same direction the progress bar on the turf list fills.
 *
 * The deep end is COVERAGE_COLORS.contacted, which is already what green means
 * everywhere else in this app: a door that is finished with.
 *
 * Deliberately not red at the empty end. Red is do-not-contact on the
 * canvasser map, and a turf nobody has started is not a warning -- it is just
 * work that is still there.
 */
export const COMPLETION_RAMP: { pct: number; color: string }[] = [
  { pct: 0,   color: '#e7e5e4' }, // stone  — untouched
  { pct: 25,  color: '#bbf7d0' },
  { pct: 50,  color: '#86efac' },
  { pct: 75,  color: '#4ade80' },
  { pct: 100, color: '#15803d' }, // the 'contacted' green — finished
];
